import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

/** Shape returned by `GET /auth/me` and embedded in login/register responses. */
export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  created_at: string;
  credits: number;
  is_available: boolean;
  last_active_at: string | null;
  failed_attempts: number;
  lockout_until: string | null;
  display_name?: string;
  birthdate?: string;
  state?: string;
  city?: string;
  neighborhood?: string;
  nationality?: string;
  phone?: string;
  gender?: string;
  account_type?: string;
  verified?: boolean;
  verification_status?: string;
  advertiser_status?: string;
  premium_until?: string | null;
  is_premium?: boolean;
}

export interface AuthContextType {
  user: User | false | null; // null = checking, false = logged out, object = logged in
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<{ requires2FA: boolean; user_id?: string }>;
  verify2FA: (user_id: string, code: string) => Promise<User>;
  register: (payload: RegisterPayload | string, email?: string, password?: string, location?: Record<string, unknown>) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  pending2FAUser: string | null;
  setPending2FAUser: (user_id: string | null) => void;
}

/** Aceita objeto único `{ name, email, password, display_name, birthdate,
 * state, city, neighborhood }`. Mantém compat com chamadas antigas que
 * passam (name, email, password, location). */
export interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  display_name?: string;
  birthdate?: string;
  state?: string;
  city?: string;
  neighborhood?: string;
  nationality?: string;
  phone?: string;
  gender?: string;
  account_type?: string;
}

const AuthCtx = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | false | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending2FAUser, setPending2FAUser] = useState<string | null>(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get<User>("/auth/me");
      setUser(data);
    } catch (err) {
      console.warn("Auth check failed, clearing token:", (err as { response?: { status?: number } })?.response?.status);
      localStorage.removeItem("access_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string, remember = false): Promise<{ requires2FA: boolean; user_id?: string }> => {
    try {
      const { data } = await api.post<{ access_token?: string; user?: User; requires_2fa?: boolean; user_id?: string }>(
        "/auth/login",
        { email, password, remember }
      );
      if (data.requires_2fa && data.user_id) {
        setPending2FAUser(data.user_id);
        return { requires2FA: true, user_id: data.user_id };
      }
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      if (data.user) setUser(data.user);
      setPending2FAUser(null);
      return { requires2FA: false };
    } catch (err) {
      const message = formatApiError(err);
      throw new Error(message);
    }
  };

  const verify2FA = async (user_id: string, code: string): Promise<User> => {
    try {
      const { data } = await api.post<{ access_token: string; user: User }>("/auth/login/2fa", {
        user_id,
        code,
      });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
      setPending2FAUser(null);
      return data.user;
    } catch (err) {
      const message = formatApiError(err);
      throw new Error(message);
    }
  };

  const register = async (
    ...args: [RegisterPayload | string, string?, string?, Record<string, unknown>?]
  ): Promise<User> => {
    try {
      let payload: RegisterPayload;
      if (args.length === 1 && args[0] && typeof args[0] === "object") {
        payload = args[0] as RegisterPayload;
      } else {
        const [name, email, password, location = {}] = args as [string, string, string, Record<string, unknown>];
        payload = { name, email, password, ...location };
      }
      const { data } = await api.post<{ access_token: string; user: User }>("/auth/register", payload);
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      const message = formatApiError(err);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, verify2FA, register, logout, refresh: checkAuth, pending2FAUser, setPending2FAUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}